import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import ExpandMore from '@mui/icons-material/ExpandMore';
import classNames from 'classnames';
import { ascendingSort } from '../../../../lib/Array';
import { CollaboratorType } from '../../../../enums/CollaboratorType';
import { composeUsername } from '../../../../lib/DisplayName';
import {
  OrganizationSchema,
  RoomCollaboratorSchema,
  UserSchema,
} from '../../../../definitions/schemas';
import { RoomType } from '../../../../enums/RoomTypes';
import { useI18n } from '../../../../lib/hook/useI18n';
import AddCollaborators from './AddCollaboratorsDialog';
import ListCollaboratorsModal from './ListCollaboratorsModal';
import OrganizationsStore from '../../../../stores/observables/OrganizationsStore';
import RoomBussinessCard from '../RoomBusinessCard';
import RoomCollaboratorsInterface from '../../../../graphql/RoomCollaborators/RoomCollaboratorsInterface';
import RoomOwner from '../../../Rooms/Save/Steps/Review/RoomOwner';
import SnackbarStore from '../../../../stores/observables/SnackbarStore';
import VisibilityToggle from '../../../Visibility/VisibilityToggle';

const useStyles = makeStyles((theme: Theme) => ({
  accordion: {
    '&.Mui-expanded': {
      margin: '0',
    },
  },
  accordionDetails: {
    padding: `${theme.spacing(1)} 0 ${theme.spacing(2)}`,
  },
  accordionHeader: {
    padding: 0,
    '& .Mui-expanded:first-of-type': {
      margin: '12px 0',
    },
  },
  dividerAccordion: {
    borderBottom: `1px solid var(--attachments-border-color)`,
  },
  cardContainerEditor: { border: `1px dashed ${theme.palette.divider}` },
  cardContainerReadonly: { border: `1px solid ${theme.palette.divider}` },
  cardContainer: {
    borderRadius: theme.spacing(1),
    margin: 'auto',
    padding: theme.spacing(0.5, 2, 1, 2),
    position: 'relative',
  },
  corner: {
    borderRight: `${theme.spacing(3)} solid ${theme.palette.background.paper}`,
    borderTopLeftRadius: theme.spacing(1),
    bottom: -1,
    height: theme.spacing(3),
    position: 'absolute',
    right: -1,
    width: theme.spacing(3),
  },
  firstAccordion: {
    paddingTop: `0`,
  },
  showAll: {
    textTransform: 'none',
  },
  visibleToggle: {
    position: 'absolute',
    right: theme.spacing(-0.5),
    top: theme.spacing(-1.25),
  },
}));

export interface ListCollaboratorProps {
  onSaveCollaboratorsChanges?: (
    collaborators: RoomCollaboratorSchema[]
  ) => void;
  roomCollaborators?: RoomCollaboratorSchema[];
  displaySelectOwner?: boolean;
}

interface Props extends ListCollaboratorProps {
  clientLogo?: string;
  disableSaveOwner?: boolean;
  hideEmail?: boolean;
  isContactCardEnabled?: boolean;
  onContactCardSectionVisibilityChange?: () => void;
  onRoomOwnerChange?: (value: UserSchema) => void;
  onSaveCollaboratorsChanges?: (
    collaborators: RoomCollaboratorSchema[]
  ) => void;
  onSaveSuccess?: () => void;
  organization: OrganizationSchema;
  owner: UserSchema;
  readOnly?: boolean;
  referrer: string;
  roomUuid?: string;
}

const ListCollaborators: React.FC<Props> = (props) => {
  const { t } = useI18n();
  const classes = useStyles();

  const {
    clientLogo,
    disableSaveOwner,
    displaySelectOwner,
    hideEmail = false,
    isContactCardEnabled = true,
    onContactCardSectionVisibilityChange,
    onRoomOwnerChange,
    onSaveCollaboratorsChanges,
    onSaveSuccess,
    organization,
    owner,
    readOnly = false,
    referrer,
    roomCollaborators = [],
    roomUuid,
  } = props;

  const [openAddCollaborators, setOpenAddCollaborators] = useState(false);
  const [openShowCollaborators, setOpenShowCollaborators] = useState(false);

  const currentOrg = OrganizationsStore.getCurrent();
  const { showAllCollaborators } = organization;

  useEffect(() => {
    if (!roomUuid && roomCollaborators && onSaveCollaboratorsChanges) {
      // update collaborators on CreateRoomWizard state for clone/create flow
      onSaveCollaboratorsChanges(roomCollaborators);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUpdateRoleDescription = async (
    collaborator: RoomCollaboratorSchema,
    roleDescription: string
  ) => {
    // only update if role description has changes
    if (
      (collaborator.roleDescription || '').toLowerCase().trim() ===
      roleDescription.toLowerCase().trim()
    ) {
      return;
    }

    if (onSaveCollaboratorsChanges) {
      const updatedCollaborators = roomCollaborators.map((roomCollaborator) => {
        if (roomCollaborator.collaboratorId === collaborator.collaboratorId) {
          return { ...roomCollaborator, roleDescription };
        }

        return roomCollaborator;
      });

      onSaveCollaboratorsChanges(updatedCollaborators);
      if (disableSaveOwner) {
        return;
      }
    }

    try {
      await new RoomCollaboratorsInterface().update({
        id: collaborator.id,
        roleDescription,
      });
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      SnackbarStore.show(`Role description updated successfully`);
    } catch (e) {
      SnackbarStore.show(`${e}`);
    }
  };

  // display room owner first
  // sort the rest collaborators by fullname
  const sortedCollaborators = useMemo(() => {
    const ownerCollaborator = roomCollaborators.find(
      ({ collaboratorId }) => collaboratorId === owner.id
    );

    const filtered = roomCollaborators
      .filter(({ id }) => !(ownerCollaborator && id === ownerCollaborator.id))
      .sort((a, b) => ascendingSort(composeUsername(a), composeUsername(b)));

    if (!ownerCollaborator) {
      return filtered;
    }

    return [ownerCollaborator, ...filtered];
  }, [roomCollaborators, owner]);

  const sellingCollaborators = useMemo(
    () =>
      sortedCollaborators.filter((collaborator) => collaborator.sellingTeam),
    [sortedCollaborators]
  );

  // If show_all_collaborators flag is on, we should always show all the collaborators
  // If show_all_collaborators flag is off, we should show 3 collaborators
  const collaboratorsAccordion = useMemo(
    () =>
      showAllCollaborators
        ? sellingCollaborators
        : sellingCollaborators.slice(0, 3),
    [sellingCollaborators, showAllCollaborators]
  );

  const collaboratorsLength = useMemo(
    () => sortedCollaborators.length,
    [sortedCollaborators]
  );

  const handleRoomOwnerChange = (user: UserSchema) => {
    if (!onRoomOwnerChange || !onSaveCollaboratorsChanges) {
      return;
    }

    const newCollaborator = {
      collaboratorId: user.id,
      collaboratorType: CollaboratorType.User,
      emailNotification: true,
      sellingTeam: true,
      organizationId: currentOrg,
      jobTitle: user.member?.jobTitle,
      roleDescription: user.member?.roleDescription,
      ...user,
    } as RoomCollaboratorSchema;

    const updatedCollaborators = roomCollaborators.filter(
      (collaborator) => collaborator.collaboratorId !== owner.id
    );

    // change owner and update collaborators in memory
    onRoomOwnerChange(user);
    onSaveCollaboratorsChanges([...updatedCollaborators, newCollaborator]);
  };

  const handleUpdateProfile = (user: UserSchema) => {
    if (onSaveSuccess) {
      onSaveSuccess();
    }

    if (!onSaveCollaboratorsChanges) {
      return;
    }

    const updatedCollaborators = roomCollaborators.map((collaborator) => {
      if (collaborator.collaboratorId !== user.id) {
        return collaborator;
      }

      return {
        ...collaborator,
        ...{
          ...user,
          jobTitle: user.member?.jobTitle,
          roleDescription: user.member?.roleDescription,
        },
      } as RoomCollaboratorSchema;
    });

    // update collaborators in memory
    onSaveCollaboratorsChanges(updatedCollaborators);
  };

  const renderVisibilityToggle = () => {
    if (!onContactCardSectionVisibilityChange) {
      return;
    }
    return (
      <Box className={classes.visibleToggle}>
        <VisibilityToggle
          enabled={!!isContactCardEnabled}
          onChange={() => {
            onContactCardSectionVisibilityChange();
          }}
          section="Profiles"
          small
        />
      </Box>
    );
  };

  const renderAccordion = (
    collaborator: RoomCollaboratorSchema,
    index: number
  ) => {
    const header = (
      <RoomBussinessCard
        disabled={!isContactCardEnabled}
        enableHeader
        hideEmail={hideEmail}
        jobTitle={collaborator.jobTitle}
        photo={collaborator.photo}
        readOnly={readOnly}
        username={composeUsername(collaborator)}
      />
    );
    const { email, jobTitle, linkedin, phoneNumber } = collaborator;

    const hasExtraDetails =
      !!jobTitle || !!linkedin || !!phoneNumber || (!hideEmail && !!email);

    if (!hasExtraDetails && readOnly) {
      return (
        <Box
          key={`accordion-collaborator-${collaborator.id}`}
          className={classNames({
            [classes.dividerAccordion]:
              collaboratorsAccordion.length - 1 !== index,
          })}
          py={1.5}
        >
          {header}
        </Box>
      );
    }

    return (
      <Accordion
        key={`accordion-collaborator-${collaborator.id}`}
        className={classNames(classes.accordion, {
          [classes.dividerAccordion]:
            collaboratorsAccordion.length - 1 !== index,
        })}
        elevation={0}
      >
        <AccordionSummary
          id={`accordion-collaborator-header-${collaborator.id}`}
          className={classes.accordionHeader}
          aria-controls={`accordion-collaborator-content-${collaborator.id}`}
          aria-label="Expand"
          expandIcon={<ExpandMore />}
        >
          {header}
        </AccordionSummary>
        <AccordionDetails
          className={classNames(classes.accordionDetails, {
            [classes.firstAccordion]: index === 0,
          })}
        >
          <RoomBussinessCard
            collaborator={collaborator}
            disabled={!isContactCardEnabled}
            editable={!readOnly}
            email={collaborator.email}
            enableBody
            firstName={collaborator.firstName}
            hideEmail={hideEmail}
            hideRoleDescription={collaboratorsAccordion.length === 1}
            jobTitle={collaborator.jobTitle}
            lastName={collaborator.lastName}
            linkedin={collaborator.linkedin}
            onRoomOwnerChange={onRoomOwnerChange}
            onSaveSuccess={onSaveSuccess}
            onUpdateProfile={handleUpdateProfile}
            onUpdateRoleDescription={(roleDescription: string) =>
              onUpdateRoleDescription(collaborator, roleDescription)
            }
            owner={
              collaborator.collaboratorId === owner?.id ? owner : undefined
            }
            phoneNumber={collaborator.phoneNumber}
            photo={collaborator.photo}
            readOnly={readOnly}
            roleDescription={collaborator.roleDescription}
            username={composeUsername(collaborator)}
          />
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Box
      className={classNames(
        classes.cardContainer,
        readOnly ? classes.cardContainerReadonly : classes.cardContainerEditor
      )}
    >
      {renderVisibilityToggle()}
      <Box
        className={classes.corner}
        sx={(theme) => ({
          borderTop: `${theme.spacing(3)} solid ${
            isContactCardEnabled
              ? theme.palette.secondary.main
              : 'var(--gray-light)'
          }`,
        })}
      />
      {!readOnly && (
        <Box mt={2}>
          <Button
            color="secondary"
            fullWidth
            onClick={() => setOpenAddCollaborators(true)}
            variant="outlined"
            data-testid="button-add-collaborators"
          >
            {collaboratorsLength > 1
              ? `Edit Collaborators (${collaboratorsLength})`
              : 'Add Collaborators'}
          </Button>
        </Box>
      )}

      {displaySelectOwner && roomCollaborators.length === 1 && owner && (
        <Box my={2} width="100%">
          <RoomOwner onRoomOwnerChange={handleRoomOwnerChange} owner={owner} />
        </Box>
      )}

      <Box mb={2}>
        {collaboratorsAccordion.map((collaborator, index) =>
          renderAccordion(collaborator, index)
        )}
      </Box>
      {((sellingCollaborators.length || 0) > 3 || showAllCollaborators) && (
        <Box mb={2.5}>
          <Button
            className={classes.showAll}
            color="secondary"
            fullWidth
            onClick={() => setOpenShowCollaborators(true)}
            size="small"
            variant="outlined"
          >
            {showAllCollaborators
              ? t('business_card.view_details')
              : t('business_card.show_all', {
                  size: sellingCollaborators.length || 0,
                })}
          </Button>
        </Box>
      )}
      {!readOnly && openAddCollaborators && (
        <AddCollaborators
          collaborators={roomCollaborators}
          disableSaveOwner={disableSaveOwner}
          onClose={() => setOpenAddCollaborators(false)}
          onRoomOwnerChange={onRoomOwnerChange}
          onSaveChanges={onSaveCollaboratorsChanges}
          onSaveSuccess={onSaveSuccess}
          open={openAddCollaborators}
          owner={owner}
          referrer={referrer}
          roomType={RoomType.Deal}
          roomUuid={roomUuid}
        />
      )}
      <ListCollaboratorsModal
        clientLogo={clientLogo}
        disableSaveOwner={disableSaveOwner}
        hideEmail={hideEmail}
        onClose={() => setOpenShowCollaborators(false)}
        onRoomOwnerChange={onRoomOwnerChange}
        onSuccess={onSaveSuccess}
        onUpdateProfile={handleUpdateProfile}
        onUpdateRoleDescription={onUpdateRoleDescription}
        open={openShowCollaborators}
        organization={organization}
        owner={owner}
        readOnly={readOnly}
        referrer={referrer}
        roomCollaborators={sellingCollaborators}
        roomUuid={roomUuid}
      />
    </Box>
  );
};

export default ListCollaborators;
